import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { GamePlayData, SkillEnum, CurrencyEnum, QuestEnum } from '@/types'
import { useGameplayContext } from './gameplayContext'
import { getRealDurationMinutes } from '@/utils'

interface GameDataSnapshot {
  timestamp: number // real time timestamp
  gameMinutes: number
  realMinutes: number
  currencies: { [key in CurrencyEnum]: number }
  skillPoints: { [key in SkillEnum]: number }
  skillLevels: { [key in SkillEnum]: number }
  completedQuestCount: number
  lastCompletedQuest?: QuestEnum
}

interface MeasurementContextType {
  snapshots: GameDataSnapshot[]
  isRecording: boolean
  startRecording: () => void
  stopRecording: () => void
  clearSnapshots: () => void
}

const MeasurementContext = createContext<MeasurementContextType | undefined>(undefined)

const SNAPSHOT_INTERVAL = 10000 // 10 seconds

export const MeasurementProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snapshots, setSnapshots] = useState<GameDataSnapshot[]>([])
  const [isRecording, setIsRecording] = useState(false)
  const [lastSnapshotTime, setLastSnapshotTime] = useState(0)
  const { gameData } = useGameplayContext()

  const takeSnapshot = useCallback(() => {
    const now = Date.now()
    // Only take snapshot if enough time has passed
    if (now - lastSnapshotTime < SNAPSHOT_INTERVAL) {
      return
    }

    const snapshot: GameDataSnapshot = {
      timestamp: now,
      gameMinutes: gameData.gameMinutes,
      realMinutes: getRealDurationMinutes(gameData.tick),
      currencies: Object.entries(gameData.currencies).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value.amount
        }),
        {} as { [key in CurrencyEnum]: number }
      ),
      skillPoints: Object.entries(gameData.skills).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value.points
        }),
        {} as { [key in SkillEnum]: number }
      ),
      skillLevels: Object.entries(gameData.skills).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value.level
        }),
        {} as { [key in SkillEnum]: number }
      ),
      completedQuestCount: gameData.completedQuests.length,
      lastCompletedQuest: gameData.completedQuests[gameData.completedQuests.length - 1]
    }

    setLastSnapshotTime(now)
    setSnapshots((prev) => [...prev, snapshot])
  }, [gameData, lastSnapshotTime])

  // Handle recording
  useEffect(() => {
    if (!isRecording) return

    // Take initial snapshot
    takeSnapshot()

    const interval = setInterval(takeSnapshot, SNAPSHOT_INTERVAL)

    return () => {
      console.log('Cleaning up recording interval')
      clearInterval(interval)
    }
  }, [isRecording, takeSnapshot])

  const startRecording = useCallback(() => {
    console.log('Starting measurement recording')
    setLastSnapshotTime(0) // Reset last snapshot time to ensure we take an initial snapshot
    setIsRecording(true)
  }, [])

  const stopRecording = useCallback(() => {
    console.log('Stopping measurement recording')
    setIsRecording(false)
  }, [])

  const clearSnapshots = useCallback(() => {
    setSnapshots([])
  }, [])

  return (
    <MeasurementContext.Provider
      value={{
        snapshots,
        isRecording,
        startRecording,
        stopRecording,
        clearSnapshots
      }}
    >
      {children}
    </MeasurementContext.Provider>
  )
}

export const useMeasurement = () => {
  const context = useContext(MeasurementContext)
  if (context === undefined) {
    throw new Error('useMeasurement must be used within a MeasurementProvider')
  }
  return context
}
